/**
 * NatalChartSvg — full natal disc.
 *
 * Renders a classic astrological wheel: outer zodiac ring (12 sectors with
 * sign glyphs and 10° tick marks), planet glyphs placed at their real
 * ecliptic longitude (`absolutePosition`) with tangential de-collision when
 * two points sit within ~7° of each other, the Asc/Desc and MC/IC axis cross,
 * faint house cusps, and major aspect lines drawn across the inner disc.
 */

import { useTranslation } from 'react-i18next';
import styles from './NatalChartSvg.module.css';
import type { ChartPlanet, ChartHouse, ChartAngles, ChartAspect } from '../../hooks/useChart';

export type NatalSelectKind = 'sign' | 'planet' | 'house' | 'angle';

export interface NatalChartSvgProps {
  planets: ChartPlanet[];
  houses?: ChartHouse[] | null;
  angles?: ChartAngles | null;
  aspects?: ChartAspect[] | null;
  size?: number;
  onSelect?: (kind: NatalSelectKind, id: string) => void;
  'data-testid'?: string;
}

const ZODIAC_ORDER = [
  'Aries', 'Taurus', 'Gemini', 'Cancer',
  'Leo', 'Virgo', 'Libra', 'Scorpio',
  'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
] as const;

const SIGN_GLYPH: Record<string, string> = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋',
  Leo: '♌', Virgo: '♍', Libra: '♎', Scorpio: '♏',
  Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
};

const PLANET_GLYPH: Record<string, string> = {
  sun: '☉', moon: '☽', mercury: '☿', venus: '♀', mars: '♂',
  jupiter: '♃', saturn: '♄', uranus: '♅', neptune: '♆', pluto: '♇',
  north_node: '☊', south_node: '☋', lilith: '⚸', chiron: '⚷',
};

const ASPECT_CLASS: Record<string, string> = {
  conjunction: styles.aspConj,
  opposition: styles.aspHard,
  square: styles.aspHard,
  trine: styles.aspSoft,
  sextile: styles.aspSoft,
};

// Convert ecliptic longitude (0=Aries 0°, increasing CCW in the sky) to SVG
// angle in degrees, with 0° = top, increasing clockwise — which matches how
// astrology wheels are conventionally drawn (Aries up, signs CCW around the
// rim → CCW on screen). Negative sign on longitude flips to screen CCW.
function lonToSvgDeg(lon: number): number {
  return -lon - 90;
}

function polar(cx: number, cy: number, r: number, deg: number): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function angularDelta(a: number, b: number): number {
  const d = Math.abs(((a - b + 540) % 360) - 180);
  return d;
}

/**
 * De-collision: if two planets are within `threshold` degrees, offset them
 * tangentially in radius so glyphs don't overlap. Returns an array of
 * per-planet radial offsets (in svg px).
 */
function spreadRadii(planets: ChartPlanet[], baseR: number, threshold = 7, step = 11): number[] {
  const n = planets.length;
  const offsets = new Array<number>(n).fill(0);
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < i; j++) {
      if (angularDelta(planets[i].absolutePosition, planets[j].absolutePosition) < threshold) {
        offsets[i] = Math.max(offsets[i], offsets[j] + step);
      }
    }
  }
  return offsets.map((o) => baseR - o);
}

export function NatalChartSvg(props: NatalChartSvgProps): JSX.Element {
  const { t } = useTranslation('me');
  const size = props.size ?? 280;
  const cx = size / 2;
  const cy = size / 2;

  // Concentric radii (outer → inner)
  const rZodiacOuter = size / 2 - 2;
  const rZodiacInner = rZodiacOuter - 22;     // zodiac band
  const rHouseRing = rZodiacInner - 4;        // faint house cusps band
  const rPlanetBase = rZodiacInner - 18;      // planet glyph base
  const rAspectHub = rPlanetBase - 22;        // inner disc where aspects live

  const planets = props.planets ?? [];
  const planetRadii = spreadRadii(planets, rPlanetBase);
  const onSelect = props.onSelect;
  const hit = onSelect ? styles.hit : undefined;

  // 12 sector dividers + sign glyphs
  const sectors = Array.from({ length: 12 }, (_, i) => {
    const lonStart = i * 30;
    const degStart = lonToSvgDeg(lonStart);
    const degMid = lonToSvgDeg(lonStart + 15);
    const pOuter = polar(cx, cy, rZodiacOuter, degStart);
    const pInner = polar(cx, cy, rZodiacInner, degStart);
    const labelPos = polar(cx, cy, (rZodiacOuter + rZodiacInner) / 2, degMid);
    return {
      sign: ZODIAC_ORDER[i],
      x1: pOuter.x, y1: pOuter.y, x2: pInner.x, y2: pInner.y,
      lx: labelPos.x, ly: labelPos.y,
    };
  });

  // 10° tick marks inside the zodiac band
  const ticks: Array<{ x1: number; y1: number; x2: number; y2: number; minor: boolean }> = [];
  for (let i = 0; i < 36; i++) {
    if (i % 3 === 0) continue; // skip the 30° dividers (already drawn)
    const deg = lonToSvgDeg(i * 10);
    const p1 = polar(cx, cy, rZodiacInner, deg);
    const p2 = polar(cx, cy, rZodiacInner + 5, deg);
    ticks.push({ x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, minor: true });
  }

  // House cusps (faint dotted lines from hub to inner ring)
  const cusps = (props.houses ?? []).map((h) => {
    const deg = lonToSvgDeg(h.absolutePosition);
    const p1 = polar(cx, cy, rAspectHub, deg);
    const p2 = polar(cx, cy, rHouseRing, deg);
    const lbl = polar(cx, cy, rHouseRing - 10, lonToSvgDeg(h.absolutePosition + 15));
    return { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, lx: lbl.x, ly: lbl.y, house: h.house };
  });

  // Asc/Desc & MC/IC axes
  const angles = props.angles;
  const axes: Array<{ id: 'asc' | 'mc'; x1: number; y1: number; x2: number; y2: number; label: string; lx: number; ly: number }> = [];
  if (angles) {
    const asc = lonToSvgDeg(angles.asc.absolutePosition);
    const dc = lonToSvgDeg(angles.dc.absolutePosition);
    const mc = lonToSvgDeg(angles.mc.absolutePosition);
    const ic = lonToSvgDeg(angles.ic.absolutePosition);
    const pAsc = polar(cx, cy, rHouseRing, asc);
    const pDc = polar(cx, cy, rHouseRing, dc);
    const pMc = polar(cx, cy, rHouseRing, mc);
    const pIc = polar(cx, cy, rHouseRing, ic);
    axes.push({
      id: 'asc',
      x1: pAsc.x, y1: pAsc.y, x2: pDc.x, y2: pDc.y,
      label: 'ASC', lx: polar(cx, cy, rHouseRing + 10, asc).x, ly: polar(cx, cy, rHouseRing + 10, asc).y,
    });
    axes.push({
      id: 'mc',
      x1: pMc.x, y1: pMc.y, x2: pIc.x, y2: pIc.y,
      label: 'MC', lx: polar(cx, cy, rHouseRing + 10, mc).x, ly: polar(cx, cy, rHouseRing + 10, mc).y,
    });
  }

  // Major aspect lines
  const planetByName = new Map(planets.map((p, idx) => [p.id, { p, idx }]));
  const aspectLines = (props.aspects ?? [])
    .filter((a) => a.isMajor && a.orb <= 6)
    .map((a, i) => {
      const e1 = planetByName.get(a.planet1);
      const e2 = planetByName.get(a.planet2);
      if (!e1 || !e2) return null;
      const d1 = lonToSvgDeg(e1.p.absolutePosition);
      const d2 = lonToSvgDeg(e2.p.absolutePosition);
      const p1 = polar(cx, cy, rAspectHub, d1);
      const p2 = polar(cx, cy, rAspectHub, d2);
      return {
        key: `${a.planet1}-${a.planet2}-${a.type}-${i}`,
        x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y,
        cls: ASPECT_CLASS[a.type] ?? styles.aspSoft,
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={styles.svg}
      data-testid={props['data-testid'] ?? 'natal-chart-svg'}
      role="img"
      aria-label={t('natalChart.title')}
    >
      <defs>
        <radialGradient id="natal-bg" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="rgba(228,185,98,0.10)" />
          <stop offset="65%" stopColor="rgba(228,185,98,0.00)" />
        </radialGradient>
      </defs>

      <circle cx={cx} cy={cy} r={rZodiacOuter} className={styles.ringOuter} />
      <circle cx={cx} cy={cy} r={rZodiacInner} className={styles.ringMid} />
      <circle cx={cx} cy={cy} r={rHouseRing} className={styles.ringInner} />
      <circle cx={cx} cy={cy} r={rAspectHub} fill="url(#natal-bg)" stroke="none" />

      {ticks.map((t, i) => (
        <line key={`tk-${i}`} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} className={styles.tickMinor} />
      ))}

      {sectors.map((s, i) => (
        <g key={`sec-${i}`}>
          <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} className={styles.sectorDivider} />
          <g
            className={hit}
            onClick={onSelect ? () => onSelect('sign', s.sign) : undefined}
            role={onSelect ? 'button' : undefined}
            tabIndex={onSelect ? 0 : undefined}
            aria-label={onSelect ? t('natalChart.about', { name: s.sign }) : undefined}
            data-testid={`natal-sign-${s.sign.toLowerCase()}`}
          >
            <circle cx={s.lx} cy={s.ly} r={11} className={styles.hitArea} />
            <text x={s.lx} y={s.ly} className={styles.signGlyph} textAnchor="middle" dominantBaseline="central">
              {SIGN_GLYPH[s.sign]}
            </text>
          </g>
        </g>
      ))}

      {cusps.map((c, i) => (
        <g key={`cu-${i}`}>
          <line x1={c.x1} y1={c.y1} x2={c.x2} y2={c.y2} className={styles.houseCusp} />
          <g
            className={hit}
            onClick={onSelect ? () => onSelect('house', String(c.house)) : undefined}
            role={onSelect ? 'button' : undefined}
            tabIndex={onSelect ? 0 : undefined}
            aria-label={onSelect ? t('natalChart.aboutHouse', { n: c.house }) : undefined}
            data-testid={`natal-house-${c.house}`}
          >
            <circle cx={c.lx} cy={c.ly} r={9} className={styles.hitArea} />
            <text x={c.lx} y={c.ly} className={styles.houseNum} textAnchor="middle" dominantBaseline="central">
              {c.house}
            </text>
          </g>
        </g>
      ))}

      {aspectLines.map((a) => (
        <line key={a.key} x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} className={a.cls} />
      ))}

      {axes.map((ax, i) => (
        <g
          key={`ax-${i}`}
          className={hit}
          onClick={onSelect ? () => onSelect('angle', ax.id) : undefined}
          role={onSelect ? 'button' : undefined}
          tabIndex={onSelect ? 0 : undefined}
          aria-label={onSelect ? t('natalChart.about', { name: ax.label }) : undefined}
          data-testid={`natal-axis-${ax.id}`}
        >
          <line x1={ax.x1} y1={ax.y1} x2={ax.x2} y2={ax.y2} className={styles.axis} />
          <circle cx={ax.lx} cy={ax.ly} r={10} className={styles.hitArea} />
          <text x={ax.lx} y={ax.ly} className={styles.axisLabel} textAnchor="middle" dominantBaseline="central">
            {ax.label}
          </text>
        </g>
      ))}

      {planets.map((p, i) => {
        const deg = lonToSvgDeg(p.absolutePosition);
        const r = planetRadii[i];
        const pos = polar(cx, cy, r, deg);
        return (
          <g
            key={p.id}
            className={hit}
            onClick={onSelect ? () => onSelect('planet', p.id) : undefined}
            role={onSelect ? 'button' : undefined}
            tabIndex={onSelect ? 0 : undefined}
            aria-label={onSelect ? t('natalChart.about', { name: p.id }) : undefined}
            data-testid={`natal-point-${p.id}`}
          >
            <circle cx={pos.x} cy={pos.y} r={11} className={styles.hitArea} />
            <circle cx={pos.x} cy={pos.y} r={9} className={styles.planetHalo} />
            <text x={pos.x} y={pos.y} textAnchor="middle" dominantBaseline="central" className={styles.planetGlyph}>
              {PLANET_GLYPH[p.id] ?? '·'}
            </text>
            {p.retrograde && (
              <text x={pos.x + 9} y={pos.y - 8} className={styles.retroMark} textAnchor="start">℞</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
